import { LanguagePicker } from 'components/language/LanguagePicker';
import TextInput from 'components/TextInput/TextInput';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import { useState, useContext, FormEvent } from 'react';
import Confirmation from 'components/Confirmation/Confirmation';
import { IntegrationContext } from '../../contexts/IntegrationContext';

export default function CredentialsForm() {
  const {
    integration,
    activateIntegration,
    createSecondarySecretKey,
    deleteSecondarySecretKey,
    switchSecondarySecretKey,
    uninstallWebApp,
  } = useContext(IntegrationContext);

  const [secretKeyPlain, setSecretKeyPlain] = useState(false);
  const [secretKeySecondaryPlain, setSecretKeySecondaryPlain] = useState(false);

  // Loading States
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Modal States
  const [modalShowPrimary, setModalShowPrimary] = useState(false);
  const [modalShowSecondary, setModalShowSecondary] = useState(false);
  const [modalShowCancel, setModalShowCancel] = useState(false);

  const [error, setError] = useState('');
  const [errorDescription, setErrorDescription] = useState('');

  const { t } = useTranslation();

  const errorCallback = (text: string, description?: string) => {
    setError(text);
    if (description) setErrorDescription(description);
    setSubmitting(false);
    setCancelling(false);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);

    activateIntegration(() => {
      setSecretKeyPlain(true);
      setSubmitting(false);
    }, errorCallback);
  };

  const confirmCancel = () => {
    setCancelling(true);

    uninstallWebApp(
      (responseData: any) => {
        if (responseData?.redirectUrl) {
          window.location.href = responseData.redirectUrl;
        } else if (integration.returnUrl) {
          window.location.href = integration.returnUrl;
        }
      },
      (err: string) => errorCallback(err)
    );
  };

  const handleCreateSecondary = () => {
    setSubmitting(true);
    createSecondarySecretKey(() => {
      setSecretKeyPlain(false);
      setSecretKeySecondaryPlain(true);
      setSubmitting(false);
    }, errorCallback);
  };

  const handleDeleteSecondary = () => {
    setSubmitting(true);
    deleteSecondarySecretKey(() => {
      setSecretKeyPlain(false);
      setSecretKeySecondaryPlain(false);
      setSubmitting(false);
    }, errorCallback);
  };

  const handleSwitchSecondary = () => {
    setSubmitting(true);
    switchSecondarySecretKey(() => {
      setSecretKeyPlain(false);
      setSecretKeySecondaryPlain(false);
      setSubmitting(false);
    }, errorCallback);
  };

  if (error) {
    return <ErrorMessage text={error} description={errorDescription} />;
  }

  return (
    <Form className="container col-12 col-lg-6" onSubmit={handleSubmit}>
      <div className="d-flex align-items-center justify-content-center mt-5">
        <img
          src={`${process.env.PUBLIC_URL}/${integration.scope}-logo.svg`}
          className="w-50"
          alt="Logo"
        />
      </div>

      <div className="row mt-5">
        <LanguagePicker />
      </div>

      <TextInput
        className="mt-5"
        name="spaceId"
        label="Space ID"
        value={integration.spaceId}
        readOnly={true}
      />

      {/* PRIMARY KEY SECTION */}
      {integration.secretKey && (
        <>
          <TextInput
            className="mt-3"
            name="secretKey"
            label="Secret Key"
            value={integration.secretKey}
            readOnly={true}
            actions={
              integration.secretKeySecondary
                ? [
                    {
                      label: 'Remove',
                      disabled: submitting,
                      onClick: () => setModalShowPrimary(true),
                    },
                  ]
                : undefined
            }
          />
          <Confirmation
            message="Are you sure you want to permanently remove the Secret Key?"
            onClick={handleSwitchSecondary}
            show={modalShowPrimary}
            setShow={setModalShowPrimary}
          />
        </>
      )}

      {secretKeyPlain && (
        <Alert variant="warning" className="mt-4">
          {t(
            'This secret key will no longer be visible! Please do not close this page until you save it.'
          )}
        </Alert>
      )}

      {/* SECONDARY KEY SECTION */}
      {integration.secretKeySecondary ? (
        <>
          <TextInput
            className="mt-3"
            name="secretKeySecondary"
            label="Secondary Secret Key"
            value={integration.secretKeySecondary}
            readOnly={true}
            actions={[
              {
                label: 'Remove',
                disabled: submitting,
                onClick: () => setModalShowSecondary(true),
              },
            ]}
          />
          <Confirmation
            message="Are you sure you want to permanently remove the Secondary Secret Key?"
            onClick={handleDeleteSecondary}
            show={modalShowSecondary}
            setShow={setModalShowSecondary}
          />
        </>
      ) : (
        /* CREATE SECONDARY BUTTON */
        integration.activated &&
        !secretKeyPlain &&
        !secretKeySecondaryPlain && (
          <div className="row mt-3 d-flex align-items-center justify-content-center">
            <div className="d-grid gap-2 d-md-block text-center">
              <Button
                variant="secondary"
                type="button"
                size="sm"
                disabled={submitting}
                onClick={handleCreateSecondary}
              >
                {submitting && (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                )}
                {t('Create Secondary Secret Key')}
              </Button>
            </div>
          </div>
        )
      )}

      {secretKeySecondaryPlain && (
        <Alert variant="warning" className="mt-4">
          {t(
            'This secondary secret key will no longer be visible! Please do not close this page until you save it.'
          )}
        </Alert>
      )}

      {/* FOOTER BUTTONS */}
      {!integration.activated ? (
        <div className="row mt-5 justify-content-center g-3">
          {/* CANCEL BUTTON */}
          <div className="col-6 col-sm-4">
            <Button
              variant="outline-secondary"
              type="button"
              className="w-100"
              disabled={submitting || cancelling}
              onClick={() => setModalShowCancel(true)}
            >
              {cancelling && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
              )}
              {t('Cancel')}
            </Button>

            {/* NEW: Confirmation for Cancel */}
            <Confirmation
              message="Are you sure you want to cancel? This will abort the setup."
              onClick={confirmCancel}
              show={modalShowCancel}
              setShow={setModalShowCancel}
            />
          </div>

          {/* SAVE BUTTON */}
          <div className="col-6 col-sm-4">
            <Button
              variant="primary"
              type="submit"
              className="w-100"
              disabled={submitting || cancelling}
            >
              {submitting && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
              )}
              {t('Save Integration')}
            </Button>
          </div>
        </div>
      ) : (
        /* RETURN BACK BUTTON */
        <div className="row mt-5 justify-content-center">
          <div className="col-12 col-sm-6 col-md-4">
            <Button
              variant="primary"
              className="w-100"
              href={integration.returnUrl || '#'}
            >
              {t('Return Back')}
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
}
